import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as XLSX from 'xlsx';
import { InputComponents } from 'types/auth';
import { DocType, stepLabel } from 'utils/importMapping';

interface DownloadTemplateSectionProps {
  processName: string;
  /** Already resolved by the parent import dialog — this section deliberately
   * does NOT fetch the flow again for itself. */
  reachableSteps: any[];
  importableFields: any[];
  displayLabel: (field: any) => string;
  showToast: (message: string, severity?: any) => void;
}

/**
 * Produces the blank spreadsheet a user fills in and then uploads.
 *
 * A section rather than a wizard step: choosing which steps to include shapes
 * the template's column list and nothing else, so it has no business gating
 * the upload flow — which steps an import actually covers is decided later, by
 * which fields the user maps a column to. Collapsed by default, since anyone
 * who already has their file doesn't need it.
 */
const DownloadTemplateSection: React.FC<DownloadTemplateSectionProps> = ({
  processName,
  reachableSteps,
  importableFields,
  displayLabel,
  showToast
}) => {
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState<DocType>('excel');
  const [includedStepUids, setIncludedStepUids] = useState<Set<string>>(new Set());

  // Only the FIRST step is selected by default. Most imports just create the
  // transaction and leave the rest of the process to be worked through
  // normally, so a template covering every step of the workflow would mostly
  // be columns the user isn't going to fill in. They add more steps if they
  // want them.
  useEffect(() => {
    setIncludedStepUids(new Set(reachableSteps.length > 0 ? [reachableSteps[0].uid] : []));
  }, [reachableSteps]);

  const templateFields = useMemo(
    () => importableFields.filter((f) => includedStepUids.has(f.__step.uid)),
    [importableFields, includedStepUids]
  );

  const fieldGuideRows = () =>
    [['Column', 'Step', 'Type', 'Required', 'Allowed values / format']].concat(
      templateFields.map((f) => {
        let hint = '';
        if (
          f.inputComponent === InputComponents.SELECT ||
          f.inputComponent === InputComponents.RADIO ||
          f.inputComponent === InputComponents.CHECKBOX
        ) {
          hint = (f.options || []).join(', ');
          if (f.inputComponent === InputComponents.CHECKBOX) hint += hint ? ' (separate multiple with a comma)' : '';
        } else if (f.inputComponent === InputComponents.DATE) {
          hint = 'e.g. 2026-08-24';
        } else if (f.inputComponent === InputComponents.RESOURCE) {
          hint = 'Enter the resource name — you will match it to a record before importing';
        } else if (f.inputComponent === InputComponents.SWITCH) {
          hint = 'true / false';
        }
        return [displayLabel(f), stepLabel(f.__step), f.inputComponent, f.settings?.required ? 'Yes' : 'No', hint];
      })
    );

  const handleDownload = () => {
    if (templateFields.length === 0) {
      showToast('Select at least one step to include in the template.', 'warning');
      return;
    }

    const headers = templateFields.map((f) => displayLabel(f));
    const fileBase = `Import_Template_${processName.replace(/\s+/g, '_')}`;

    if (docType === 'csv') {
      // A CSV holds exactly one sheet, so the Field Guide can't ride along as
      // a second one — XLSX.writeFile to a .csv name silently writes only the
      // first sheet and drops the rest. Two files instead of one silently
      // incomplete one.
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([headers]), 'Data');
      XLSX.writeFile(wb, `${fileBase}.csv`);

      const guideWb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(guideWb, XLSX.utils.aoa_to_sheet(fieldGuideRows()), 'Field Guide');
      XLSX.writeFile(guideWb, `${fileBase}_Field_Guide.csv`);
      showToast('Downloaded the template and its field guide as two CSV files.', 'success');
      return;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([headers]), 'Data');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(fieldGuideRows()), 'Field Guide');
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  const handleStepsChange = (uids: string[]) => setIncludedStepUids(new Set(uids));

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, cursor: 'pointer' }}
      >
        <Box>
          <Typography variant="subtitle2">Download a template</Typography>
          <Typography variant="caption" color="text.secondary">
            A blank file with one column per field in this process — fill it in, then upload it below.
          </Typography>
        </Box>
        <IconButton size="small" sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 1.5, mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="template-doc-type-label">Document Type</InputLabel>
              <Select
                labelId="template-doc-type-label"
                label="Document Type"
                value={docType}
                onChange={(e) => setDocType(e.target.value as DocType)}
              >
                <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                <MenuItem value="csv">CSV (.csv)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="template-steps-label">Steps to include</InputLabel>
              <Select
                labelId="template-steps-label"
                label="Steps to include"
                multiple
                value={reachableSteps.filter((s) => includedStepUids.has(s.uid)).map((s) => s.uid)}
                onChange={(e) => handleStepsChange(e.target.value as string[])}
                renderValue={(selected: any) => `${selected.length}/${reachableSteps.length} step${reachableSteps.length === 1 ? '' : 's'}`}
              >
                {reachableSteps.map((step) => (
                  <MenuItem key={step.uid} value={step.uid}>
                    <Checkbox size="small" checked={includedStepUids.has(step.uid)} sx={{ p: 0, mr: 1 }} />
                    <ListItemText primary={stepLabel(step)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button size="small" onClick={() => handleStepsChange(reachableSteps.map((s) => s.uid))}>
              Select all
            </Button>
            <Button size="small" onClick={() => handleStepsChange([])}>
              Select none
            </Button>

            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={templateFields.length === 0}>
              Download Template
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block">
            {templateFields.length} column{templateFields.length === 1 ? '' : 's'} will be included. Add one row per transaction you want to
            create.
            {docType === 'excel'
              ? ' A second "Field Guide" sheet lists each column\'s type, whether it is required, and its allowed values.'
              : ' The field guide downloads as a second CSV file, since a CSV can only hold one sheet.'}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DownloadTemplateSection;
